// More accurate kelvin to RGB conversion based on user perception in lighting applications

// User-perception-based implementation for lighting applications
// This matches how people actually perceive color temperature in real lighting scenarios
export const kelvinToRGB = (kelvin) => {
  // Clamp to valid range
  const temp = Math.max(1000, Math.min(40000, kelvin));

  let red, green, blue;

  // Smooth red transition: full at warm temps, gradually reduce for cool temps
  if (temp <= 3000) {
    red = 255;
  } else if (temp <= 6000) {
    // Gradual reduction to reach white balance
    const factor = (temp - 3000) / 3000;
    red = 255 - 15 * factor; // Small reduction for white balance
  } else {
    // Very gentle reduction for light blue
    const factor = Math.min(1, (temp - 6000) / 6000);
    red = 240 - 15 * factor; // Gentle reduction, staying high
  }
  red = Math.max(0, Math.min(255, red));

  // Smooth green transition: low at orange, peak at yellow/white, slight reduction for blue
  if (temp <= 2000) {
    // Orange range - lower green
    const factor = (temp - 1000) / 1000;
    green = 100 + 80 * factor;
  } else if (temp <= 5500) {
    // Yellow to white range - increase green
    const factor = (temp - 2000) / 3500;
    green = 180 + 75 * factor;
  } else {
    // Light blue range - slight green reduction but keep high
    const factor = Math.min(1, (temp - 5500) / 6500);
    green = 255 - 10 * factor;
  }
  green = Math.max(0, Math.min(255, green));

  // Smooth blue transition: very low at warm, gradual increase to white, max for light blue
  if (temp <= 2500) {
    // Orange to yellow range - very low blue
    const factor = Math.max(0, (temp - 1000) / 1500);
    blue = 50 * factor;
  } else if (temp <= 5500) {
    // Yellow to white range - gradual blue increase
    const factor = (temp - 2500) / 3000;
    blue = 50 + 205 * Math.pow(factor, 1.2);
  } else {
    // Light blue range - keep blue high
    blue = 255;
  }
  blue = Math.max(0, Math.min(255, blue));

  return {
    r: Math.round(red),
    g: Math.round(green),
    b: Math.round(blue),
  };
};

// Convert hex color to HSL
export const hexToHsl = (hex) => {
  hex = hex.replace(/^#/, "");
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Convert RGB string to HSL
export const rgbToHsl = (rgbString) => {
  const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return { h: 0, s: 0, l: 50 };

  const r = parseInt(match[1]) / 255;
  const g = parseInt(match[2]) / 255;
  const b = parseInt(match[3]) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Common function to get display color from RGB or temperature
export const getDisplayColor = (rgbColor, tempColor, mode = "rgb") => {
  let baseColor;

  if (mode === "rgb" && rgbColor) {
    const selectedColor = rgbColor.startsWith("#") ? rgbColor : `#${rgbColor}`;
    baseColor = selectedColor;
  } else if (mode === "temperature" && tempColor) {
    const rgb = kelvinToRGB(tempColor);
    baseColor = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    return baseColor; // Return early for temperature mode
  } else {
    return "#e6e6e6"; // Fallback color
  }

  // Convert to HSL and adjust lightness if saturation is low
  let hsl;
  if (baseColor.startsWith("#")) {
    hsl = hexToHsl(baseColor);
  } else if (baseColor.startsWith("rgb")) {
    hsl = rgbToHsl(baseColor);
  } else {
    return baseColor; // Fallback to original color
  }

  // Adjust lightness for low saturation colors
  hsl.l = 90 - hsl.s / 2;

  return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
};
